/**
 * EchoHire — single source of truth for brand, light/dark themes, spacing, and breakpoints.
 * Import this file for tokens; do not hardcode colors or spacing elsewhere in new UI code.
 */

// ——— Brand ———
const BRAND = '#009999';
const BRAND_RGB = '0, 153, 153';

const brandScale = {
  50: '#e6f7f7',
  100: '#b3ebeb',
  200: '#80dfdf',
  300: '#4dd3d3',
  400: '#1ac7c7',
  500: BRAND,
  600: '#007a7a',
  700: '#005c5c',
  800: '#003d3d',
  900: '#001f1f',
};

// ——— Spacing (px) — 4px base grid ———
const spacing = {
  0: '0',
  px: '1px',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  3.5: '14px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  9: '36px',
  10: '40px',
  11: '44px',
  12: '48px',
  14: '56px',
  16: '64px',
  20: '80px',
  24: '96px',
};

// MUI spacing multiplier (1 unit = 8px) aligned to our scale names
const muiSpacing = (factor) => `${factor * 8}px`;

// ——— Layout ———
const radii = {
  none: '0',
  sm: '6px',
  md: '10px',
  lg: '14px',
  xl: '20px',
  full: '9999px',
};

const fontFamily = {
  sans: '"Inter", "Roboto", system-ui, -apple-system, sans-serif',
  mono: '"JetBrains Mono", "Fira Code", ui-monospace, monospace',
};

const fontSize = {
  xs: ['0.75rem', { lineHeight: '1rem' }],
  sm: ['0.875rem', { lineHeight: '1.25rem' }],
  base: ['1rem', { lineHeight: '1.5rem' }],
  lg: ['1.125rem', { lineHeight: '1.75rem' }],
  xl: ['1.25rem', { lineHeight: '1.75rem' }],
  '2xl': ['1.5rem', { lineHeight: '2rem' }],
  '3xl': ['1.875rem', { lineHeight: '2.25rem' }],
  '4xl': ['2.25rem', { lineHeight: '2.5rem' }],
};

// ——— Responsive breakpoints (px) — use in JS + Tailwind ———
const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

const containers = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1400px',
  content: '1200px',
  narrow: '480px',
};

const status = {
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: BRAND,
  neutral: '#94a3b8',
};

// ——— Semantic themes ———
const themes = {
  light: {
    bg: {
      default: '#f8fafb',
      elevated: '#ffffff',
      muted: '#eef1f4',
      sidebar: 'rgba(255, 255, 255, 0.92)',
      overlay: 'rgba(15, 23, 42, 0.45)',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
      muted: '#94a3b8',
      inverse: '#ffffff',
      onBrand: '#ffffff',
    },
    border: {
      default: '#e2e8f0',
      strong: '#cbd5e1',
      focus: BRAND,
    },
    brand: brandScale,
    status,
    shadow: {
      sm: '0 1px 2px rgba(15, 23, 42, 0.06)',
      md: '0 4px 12px rgba(15, 23, 42, 0.08)',
      lg: '0 12px 32px rgba(15, 23, 42, 0.12)',
      brand: `0 4px 24px rgba(${BRAND_RGB}, 0.22)`,
    },
    sidebar: {
      active: `rgba(${BRAND_RGB}, 0.1)`,
      hover: `rgba(${BRAND_RGB}, 0.06)`,
    },
  },
  dark: {
    bg: {
      default: '#0b0f14',
      elevated: '#141b22',
      muted: '#1a222c',
      sidebar: 'rgba(20, 27, 34, 0.95)',
      overlay: 'rgba(0, 0, 0, 0.6)',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#94a3b8',
      muted: '#64748b',
      inverse: '#0f172a',
      onBrand: '#ffffff',
    },
    border: {
      default: '#2a3441',
      strong: '#3d4a5c',
      focus: brandScale[400],
    },
    brand: brandScale,
    status: {
      ...status,
      info: brandScale[400],
    },
    shadow: {
      sm: '0 1px 2px rgba(0, 0, 0, 0.35)',
      md: '0 4px 16px rgba(0, 0, 0, 0.4)',
      lg: '0 12px 40px rgba(0, 0, 0, 0.5)',
      brand: `0 4px 28px rgba(${BRAND_RGB}, 0.35)`,
    },
    sidebar: {
      active: `rgba(${BRAND_RGB}, 0.18)`,
      hover: `rgba(${BRAND_RGB}, 0.1)`,
    },
  },
};

const STORAGE_KEY = 'echohire-theme-mode';

function getSystemMode() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveThemeMode(mode) {
  if (mode === 'light' || mode === 'dark') return mode;
  return getSystemMode();
}

/** HSL channels for shadcn: `hsl(var(--primary) / <alpha>)` */
function hexToHslChannels(hex) {
  const c = hex.replace('#', '');
  let r = parseInt(c.slice(0, 2), 16) / 255;
  let g = parseInt(c.slice(2, 4), 16) / 255;
  let b = parseInt(c.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function getShadcnCssVars(themeTokens) {
  const t = themeTokens;
  return {
    background: hexToHslChannels(t.bg.default),
    foreground: hexToHslChannels(t.text.primary),
    card: hexToHslChannels(t.bg.elevated),
    'card-foreground': hexToHslChannels(t.text.primary),
    popover: hexToHslChannels(t.bg.elevated),
    'popover-foreground': hexToHslChannels(t.text.primary),
    primary: hexToHslChannels(t.brand[500]),
    'primary-foreground': hexToHslChannels(t.text.onBrand),
    secondary: hexToHslChannels(t.bg.muted),
    'secondary-foreground': hexToHslChannels(t.text.primary),
    muted: hexToHslChannels(t.bg.muted),
    'muted-foreground': hexToHslChannels(t.text.secondary),
    accent: hexToHslChannels(t.brand[50]),
    'accent-foreground': hexToHslChannels(t.brand[700]),
    destructive: hexToHslChannels(t.status.error),
    'destructive-foreground': '0 0% 100%',
    border: hexToHslChannels(t.border.default),
    input: hexToHslChannels(t.border.strong),
    ring: hexToHslChannels(t.border.focus),
    radius: radii.md,
  };
}

const shadcnTailwindColors = {
  border: 'hsl(var(--border) / <alpha-value>)',
  input: 'hsl(var(--input) / <alpha-value>)',
  ring: 'hsl(var(--ring) / <alpha-value>)',
  background: 'hsl(var(--background) / <alpha-value>)',
  foreground: 'hsl(var(--foreground) / <alpha-value>)',
  card: {
    DEFAULT: 'hsl(var(--card) / <alpha-value>)',
    foreground: 'hsl(var(--card-foreground) / <alpha-value>)',
  },
  popover: {
    DEFAULT: 'hsl(var(--popover) / <alpha-value>)',
    foreground: 'hsl(var(--popover-foreground) / <alpha-value>)',
  },
  primary: {
    DEFAULT: 'hsl(var(--primary) / <alpha-value>)',
    foreground: 'hsl(var(--primary-foreground) / <alpha-value>)',
  },
  secondary: {
    DEFAULT: 'hsl(var(--secondary) / <alpha-value>)',
    foreground: 'hsl(var(--secondary-foreground) / <alpha-value>)',
  },
  muted: {
    DEFAULT: 'hsl(var(--muted) / <alpha-value>)',
    foreground: 'hsl(var(--muted-foreground) / <alpha-value>)',
  },
  accent: {
    DEFAULT: 'hsl(var(--accent) / <alpha-value>)',
    foreground: 'hsl(var(--accent-foreground) / <alpha-value>)',
  },
  destructive: {
    DEFAULT: 'hsl(var(--destructive) / <alpha-value>)',
    foreground: 'hsl(var(--destructive-foreground) / <alpha-value>)',
  },
};

function applyCssTheme(mode) {
  const resolved = resolveThemeMode(mode);
  const t = themes[resolved];
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (!root || !t) return resolved;

  const flat = {
    'color-brand': t.brand[500],
    'color-brand-hover': t.brand[600],
    'color-brand-muted': t.brand[100],
    'color-bg': t.bg.default,
    'color-bg-elevated': t.bg.elevated,
    'color-bg-muted': t.bg.muted,
    'color-bg-sidebar': t.bg.sidebar,
    'color-text': t.text.primary,
    'color-text-secondary': t.text.secondary,
    'color-text-muted': t.text.muted,
    'color-text-on-brand': t.text.onBrand,
    'color-border': t.border.default,
    'color-border-strong': t.border.strong,
    'color-border-focus': t.border.focus,
    'color-success': t.status.success,
    'color-warning': t.status.warning,
    'color-error': t.status.error,
    'color-info': t.status.info,
    'shadow-sm': t.shadow.sm,
    'shadow-md': t.shadow.md,
    'shadow-lg': t.shadow.lg,
    'shadow-brand': t.shadow.brand,
    'sidebar-active': t.sidebar.active,
    'sidebar-hover': t.sidebar.hover,
    'radius-sm': radii.sm,
    'radius-md': radii.md,
    'radius-lg': radii.lg,
    'font-sans': fontFamily.sans,
    'container-content': containers.content,
  };

  Object.entries(flat).forEach(([key, value]) => {
    root.style.setProperty(`--eh-${key}`, value);
  });

  Object.entries(spacing).forEach(([key, value]) => {
    root.style.setProperty(`--eh-space-${key}`, value);
  });

  Object.entries(breakpoints).forEach(([key, value]) => {
    root.style.setProperty(`--eh-bp-${key}`, `${value}px`);
  });

  Object.entries(getShadcnCssVars(t)).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });

  root.style.setProperty('--eh-brand-rgb', BRAND_RGB);
  root.setAttribute('data-theme', resolved);
  root.classList.toggle('dark', resolved === 'dark');

  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolved === 'dark' ? t.bg.elevated : BRAND);
  }

  return resolved;
}

function createMuiThemeOptions(mode) {
  const resolved = resolveThemeMode(mode);
  const t = themes[resolved];

  return {
    palette: {
      mode: resolved,
      primary: {
        main: t.brand[500],
        light: t.brand[400],
        dark: t.brand[700],
        contrastText: t.text.onBrand,
      },
      secondary: {
        main: t.brand[300],
        dark: t.brand[600],
        light: t.brand[200],
      },
      background: {
        default: t.bg.default,
        paper: t.bg.elevated,
        sidebar: t.bg.sidebar,
      },
      text: {
        primary: t.text.primary,
        secondary: t.text.secondary,
      },
      divider: t.border.default,
      error: { main: t.status.error },
      success: { main: t.status.success },
      warning: { main: t.status.warning },
      info: { main: t.status.info },
      custom: {
        glow: t.shadow.brand,
        shadow: t.shadow.md,
        gradient: `linear-gradient(135deg, ${t.bg.muted} 0%, ${t.bg.default} 100%)`,
        sidebarActive: t.sidebar.active,
        sidebarHover: t.sidebar.hover,
      },
    },
    typography: {
      fontFamily: fontFamily.sans,
    },
    shape: {
      borderRadius: parseInt(radii.md, 10),
    },
    breakpoints: {
      values: {
        xs: breakpoints.xs,
        sm: breakpoints.sm,
        md: breakpoints.md,
        lg: breakpoints.lg,
        xl: breakpoints.xl,
      },
    },
    spacing: 8,
  };
}

/** Legacy shape — keeps existing pages working during migration */
const colors = {
  primary: {
    navy: brandScale[900],
    deep: brandScale[700],
    dark: brandScale[500],
    medium: brandScale[400],
    light: brandScale[300],
    lighter: brandScale[200],
    skyLight: brandScale[100],
    pale: brandScale[50],
    faint: '#f0fcfc',
  },
  neutral: {
    offWhite: themes.light.bg.default,
    paleGray: themes.light.bg.muted,
    border: themes.light.border.default,
    charcoal: themes.light.text.primary,
    slate: themes.light.text.secondary,
  },
  status,
  accent: {
    electricCyan: brandScale[400],
    indigoViolet: '#5c6bc0',
    frostedBlue: brandScale[50],
  },
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    400: '#9ca3af',
    600: '#4b5563',
    800: '#1f2937',
    900: '#111827',
  },
};

function readableTextColor(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? colors.gray[900] : '#ffffff';
}

const tailwindThemeExtend = {
  colors: {
    ...shadcnTailwindColors,
    brand: {
      DEFAULT: BRAND,
      ...brandScale,
    },
    surface: {
      DEFAULT: 'var(--eh-color-bg)',
      elevated: 'var(--eh-color-bg-elevated)',
      muted: 'var(--eh-color-bg-muted)',
    },
    foreground: {
      DEFAULT: 'var(--eh-color-text)',
      secondary: 'var(--eh-color-text-secondary)',
      muted: 'var(--eh-color-text-muted)',
    },
    border: {
      DEFAULT: 'var(--eh-color-border)',
      strong: 'var(--eh-color-border-strong)',
    },
    success: 'var(--eh-color-success)',
    warning: 'var(--eh-color-warning)',
    error: 'var(--eh-color-error)',
    info: 'var(--eh-color-info)',
  },
  spacing,
  borderRadius: radii,
  fontFamily: {
    sans: fontFamily.sans.split(',').map((f) => f.trim().replace(/"/g, '')),
    mono: fontFamily.mono.split(',').map((f) => f.trim().replace(/"/g, '')),
  },
  fontSize,
  screens: {
    sm: `${breakpoints.sm}px`,
    md: `${breakpoints.md}px`,
    lg: `${breakpoints.lg}px`,
    xl: `${breakpoints.xl}px`,
    '2xl': `${breakpoints['2xl']}px`,
  },
  maxWidth: {
    content: containers.content,
    narrow: containers.narrow,
  },
  boxShadow: {
    sm: 'var(--eh-shadow-sm)',
    md: 'var(--eh-shadow-md)',
    lg: 'var(--eh-shadow-lg)',
    brand: 'var(--eh-shadow-brand)',
  },
};

const responsive = {
  breakpoints,
  containers,
  /** Padding for page content — use in sx/className */
  pagePadding: {
    xs: spacing[4],
    sm: spacing[6],
    md: spacing[8],
  },
  /** Minimum tap target (accessibility) */
  minTouchTarget: '44px',
};

export {
  BRAND,
  BRAND_RGB,
  brandScale,
  spacing,
  muiSpacing,
  radii,
  fontFamily,
  fontSize,
  breakpoints,
  containers,
  status,
  themes,
  STORAGE_KEY,
  getSystemMode,
  resolveThemeMode,
  applyCssTheme,
  createMuiThemeOptions,
  colors,
  readableTextColor,
  tailwindThemeExtend,
  shadcnTailwindColors,
  getShadcnCssVars,
  hexToHslChannels,
  responsive,
};
